import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Info,
  Loader2,
  RefreshCcw,
  Sparkles,
  X,
} from "lucide-react";
import { answerCfoAction, getCfoActions, suggestCfoPlaceCountry } from "@/api/cfoActions";
import type {
  CfoAction,
  CfoActionAnswerRead,
  CfoActionList,
  CfoFounderPlace,
  CfoPlaceSuggestion,
  CfoProofKind,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { CfoCountryPicker } from "./CfoCountryPicker";

/** Same floor as the backend's sourced-review rule. */
const MIN_REASON = 12;
const POLL_MS = 4000;
/** About three minutes of polling after an answer, then manual refresh. */
const POLL_LIMIT = 45;

const DECISIONS: Record<string, string> = {
  QUALIFIED: "Qualified",
  NOT_QUALIFIED: "Not qualified",
  NEEDS_REVIEW: "Needs review",
  NEEDS_DATA: "Missing data",
};

/** Why the re-scoring was not started on the reviewer's behalf. */
const REFUSALS: Record<string, string> = {
  CFO_PERMISSION_REVOKED:
    "your role cannot run the scoring engine. Someone with Engine access has to request the reevaluation.",
  CFO_ACTOR_REVOKED: "your account cannot run the scoring engine.",
  CFO_EXECUTION_STOPPED:
    "the CFO engine is stopped. The answer counts at the next evaluation.",
  CFO_JOB_STOPPED:
    "the CFO engine is stopped. The answer counts at the next evaluation.",
  CFO_WORK_PAUSED: "re-scoring is paused for this company.",
  CFO_COMPANY_ARCHIVED: "the company is archived.",
};

/** Answers refused because the page is behind the server. */
const STALE: Record<string, string> = {
  CFO_ACTIONS_CHANGED:
    "The evaluation changed since this page was loaded. The actions were refreshed.",
  CFO_ACTION_NOT_ANSWERABLE:
    "This action can no longer be answered. The actions were refreshed.",
  CFO_ACTION_VALUE_NOT_ALLOWED:
    "This value is no longer offered. The actions were refreshed.",
  CFO_FOUNDER_NOT_OFFERED:
    "This founder can no longer be documented. The actions were refreshed.",
  CFO_PROOF_KIND_NOT_OFFERED:
    "This kind of item is no longer accepted. The actions were refreshed.",
  CFO_FOUNDER_PLACE_REQUIRED: "The actions changed. They were refreshed.",
  CFO_FOUNDER_PLACE_NOT_EXPECTED: "The actions changed. They were refreshed.",
};

/** Opens one action's form, pre-filled (from the research sheet). A new
 *  `key` opens it again with the same values. */
export interface CfoActionRequest {
  key: number;
  actionId: string;
  founderId?: string;
  sourceUrl?: string | null;
}

function decisionLabel(decision: string | null | undefined): string {
  if (!decision) return "—";
  return DECISIONS[decision] ?? decision.replace(/_/g, " ").toLowerCase();
}

function errorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = (error.response?.data as { detail?: unknown } | undefined)
    ?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    for (const item of detail) {
      const msg = (item as { msg?: unknown } | null)?.msg;
      const match = typeof msg === "string" ? msg.match(/CFO_[A-Z_]+/) : null;
      if (match) return match[0];
    }
  }
  return null;
}

/**
 * What blocks the CFO decision of this company, answerable in place:
 * a founder place no table reads that decides the outcome (choose its
 * country), or a missing scoring input (fill it). An answer is a signed
 * manual source; the company is then re-scored and this panel follows
 * the new evaluation. Renders nothing when there is nothing to do or
 * the viewer may not see founder data.
 */
export function CfoActionsPanel({
  slug,
  companyId,
  canAnswer,
  onRescored,
  onAnswered,
  onLoaded,
  request,
}: {
  slug: string;
  companyId: number;
  canAnswer: boolean;
  /** Fired once a new evaluation is published after an input changed. */
  onRescored?: () => void;
  /** Fired after each saved answer, with the manual source it recorded. */
  onAnswered?: (result: CfoActionAnswerRead, action: CfoAction) => void;
  /** Fired with every list read, for a container that follows the state. */
  onLoaded?: (list: CfoActionList) => void;
  request?: CfoActionRequest | null;
}) {
  const [data, setData] = useState<CfoActionList | null>(null);
  const [failure, setFailure] = useState<"hidden" | "error" | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);
  const [rescored, setRescored] = useState<string | null>(null);
  const [notQueued, setNotQueued] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const generation = useRef(0);
  const lastState = useRef<CfoActionList["state"] | null>(null);
  const rescoredRef = useRef(onRescored);
  const answeredRef = useRef(onAnswered);
  const loadedRef = useRef(onLoaded);
  useEffect(() => {
    rescoredRef.current = onRescored;
    answeredRef.current = onAnswered;
    loadedRef.current = onLoaded;
  }, [onRescored, onAnswered, onLoaded]);
  // A form opened from outside, pre-filled; keyed so the same request reopens it.
  const [prefill, setPrefill] = useState<CfoActionRequest | null>(null);
  useEffect(() => {
    if (!request) return;
    setPrefill(request);
    setOpenId(request.actionId);
  }, [request]);

  const load = useCallback(async () => {
    const current = ++generation.current;
    try {
      const value = await getCfoActions(slug, companyId);
      if (generation.current !== current) return;
      if (lastState.current === "REEVALUATING" && value.state === "CURRENT") {
        setRescored(value.decision ?? "");
        setNotQueued(null);
        rescoredRef.current?.();
      }
      lastState.current = value.state;
      setFailure(null);
      setData(value);
      loadedRef.current?.(value);
    } catch (error) {
      if (generation.current !== current) return;
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      setFailure(status === 403 || status === 404 ? "hidden" : "error");
    }
  }, [slug, companyId]);

  useEffect(() => {
    const requests = generation;
    lastState.current = null;
    void load();
    return () => {
      requests.current++;
    };
  }, [load]);

  // Follow the re-scoring until the new evaluation is published.
  useEffect(() => {
    if (data?.state !== "REEVALUATING" || polls >= POLL_LIMIT) return;
    const timer = window.setTimeout(() => {
      setPolls((n) => n + 1);
      void load();
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [data, polls, load]);

  // A country suggested from the place label alone, to pre-select a place's answer.
  const suggest = useCallback(
    (actionId: string) => suggestCfoPlaceCountry(slug, companyId, actionId),
    [slug, companyId],
  );

  async function refresh() {
    setRefreshing(true);
    setPolls(0);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }

  async function submit(
    action: CfoAction,
    value: string,
    reason: string,
    place?: CfoFounderPlace,
  ) {
    if (!data?.assessment_id) return;
    const send = (assessmentId: string) =>
      answerCfoAction(slug, companyId, {
        expected_assessment_id: assessmentId,
        action_id: action.id,
        value,
        reason: reason.trim() || null,
        ...(place ? { founder_place: place } : {}),
      });
    try {
      let result: CfoActionAnswerRead;
      try {
        result = await send(data.assessment_id);
      } catch (error) {
        // Re-evaluated meanwhile (a change is often followed by a second pass): the same
        // question, still asked with this answer offered, is answered on the new one.
        if (errorCode(error) !== "CFO_ACTIONS_CHANGED") throw error;
        const fresh = await getCfoActions(slug, companyId);
        const same =
          fresh.state === "CURRENT"
            ? (fresh.actions ?? []).find(
                (a) =>
                  a.id === action.id &&
                  a.kind === action.kind &&
                  (a.choices ?? []).some((c) => c.value === value) &&
                  (!place || (a.founders ?? []).some((f) => f.value === place.founder_id)),
              )
            : undefined;
        if (!same || !fresh.assessment_id) throw error;
        result = await send(fresh.assessment_id);
      }
      answeredRef.current?.(result, action);
      if (result.reevaluation.queued) {
        setNotQueued(null);
        toast.success("Answer saved", {
          description:
            result.reevaluation.reason === "SERVICE_MANDATE"
              ? "The company is re-scored automatically with it."
              : "The company is being re-scored with it.",
        });
      } else {
        const code = result.reevaluation.reason ?? "";
        setNotQueued(code);
        toast.warning("Answer saved, re-scoring not started", {
          description: REFUSALS[code] ?? code,
        });
      }
      setOpenId(null);
      setRescored(null);
      setPolls(0);
      // An input changed: even if the new evaluation is already published
      // when the list comes back, it is followed as a re-scoring.
      lastState.current = "REEVALUATING";
      await load();
    } catch (error) {
      const code = errorCode(error);
      if (code && STALE[code]) {
        toast.error(STALE[code]);
        setOpenId(null);
        void load();
      } else if (axios.isAxiosError(error) && error.response?.status === 403) {
        toast.error("You are not allowed to answer CFO actions.");
      } else if (code === "CFO_SOURCED_REVIEW_REQUIRED") {
        toast.error(`The justification needs at least ${MIN_REASON} characters.`);
      } else if (code === "CFO_FOUNDER_PLACE_SOURCE_URL") {
        toast.error("The source must be a web address, e.g. https://www.linkedin.com/in/…");
      } else {
        toast.error("The answer was not saved. Try again.");
      }
    }
  }

  if (failure === "hidden") return null;
  if (failure === "error") {
    return (
      <div
        role="status"
        className="flex items-center justify-between gap-4 rounded-plane bg-white px-5 py-3 text-sm text-slate-500 shadow-plane"
      >
        <span>CFO actions could not be loaded.</span>
        <Button variant="ghost" size="sm" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    );
  }
  if (!data || data.state === "NONE") return null;

  const refreshButton = (
    <Button
      variant="ghost"
      size="sm"
      aria-label="Refresh actions"
      title="Refresh actions"
      disabled={refreshing}
      onClick={() => void refresh()}
    >
      <RefreshCcw className={cn(refreshing && "animate-spin")} />
    </Button>
  );

  if (data.state === "REEVALUATING") {
    const waiting = notQueued !== null;
    return (
      <section
        aria-label="CFO actions"
        className="flex items-start justify-between gap-4 rounded-plane border border-sky-200 bg-sky-50/70 p-5 shadow-plane"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
            {waiting || polls >= POLL_LIMIT ? (
              <Clock className="h-4 w-4" />
            ) : (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
          </span>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              {waiting ? "Waiting for re-scoring" : "Re-scoring in progress"}
            </h2>
            <p className="mt-0.5 text-xs text-slate-600">
              {waiting
                ? `Your answer is saved. Re-scoring was not started on your behalf: ${REFUSALS[notQueued] ?? notQueued}`
                : polls >= POLL_LIMIT
                  ? "An input changed and the new evaluation is not published yet. Refresh later."
                  : "An input changed (an answer or a new source). The actions update once the new evaluation is published."}
            </p>
          </div>
        </div>
        {refreshButton}
      </section>
    );
  }

  const actions = data.actions ?? [];
  if (actions.length === 0) {
    if (rescored === null) return null;
    return (
      <section
        aria-label="CFO actions"
        role="status"
        className="flex items-center justify-between gap-4 rounded-plane border border-emerald-200 bg-emerald-50/70 px-5 py-3 shadow-plane"
      >
        <p className="flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Re-scored: {decisionLabel(rescored)}. Nothing left to answer.
        </p>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Dismiss"
          onClick={() => setRescored(null)}
        >
          <X />
        </Button>
      </section>
    );
  }

  const answerable = actions.filter((a) => a.kind !== "INFO");
  const infos = actions.filter((a) => a.kind === "INFO");
  const summary =
    answerable.length === 0
      ? "Blocked by the points below. They are resolved from their sources, not answered here."
      : data.decision === "NEEDS_REVIEW"
        ? "A founder place decides the outcome and no table reads it. Choose its country: the company is then re-scored."
        : answerable.some((a) => a.kind === "FOUNDER_PLACE")
          ? "Inputs are missing, a founder's documented place among them. Fill in what you find: the company is then re-scored."
          : "A scoring input is missing. Fill it in: the company is then re-scored.";

  return (
    <section
      aria-label="CFO actions"
      className="rounded-plane border border-amber-200 bg-amber-50/60 p-5 shadow-plane"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div>
            <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
              Action required
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {decisionLabel(data.decision)}
              </span>
            </h2>
            <p className="mt-0.5 text-xs text-slate-600">{summary}</p>
            {data.evaluated_at && (
              <p className="mt-0.5 text-xs text-slate-400">
                Evaluated {formatDate(data.evaluated_at)}
              </p>
            )}
          </div>
        </div>
        {refreshButton}
      </div>
      {rescored !== null && (
        <p className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Re-scored: {decisionLabel(rescored)}.
        </p>
      )}
      {answerable.length > 0 && (
        <ul className="mt-4 space-y-3">
          {answerable.map((action) => (
            <ActionItem
              key={`${action.id}:${prefill?.actionId === action.id ? prefill.key : 0}`}
              action={action}
              open={openId === action.id}
              canAnswer={canAnswer}
              prefill={prefill?.actionId === action.id ? prefill : null}
              onOpen={() => setOpenId(action.id)}
              onCancel={() => {
                setOpenId(null);
                setPrefill(null);
              }}
              onSubmit={(value, reason, place) => submit(action, value, reason, place)}
              suggest={suggest}
            />
          ))}
        </ul>
      )}
      {infos.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {infos.map((action) => (
            <li
              key={action.id}
              className="flex items-start gap-2 text-xs text-slate-600"
            >
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-slate-400" />
              {action.title}
            </li>
          ))}
        </ul>
      )}
      {!canAnswer && answerable.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          Answering needs the right to edit companies or to do enrichment work.
        </p>
      )}
    </section>
  );
}

type Submit = (value: string, reason: string, place?: CfoFounderPlace) => Promise<void>;
type Suggest = (actionId: string) => Promise<CfoPlaceSuggestion>;

function ActionItem({
  action,
  open,
  canAnswer,
  prefill,
  onOpen,
  onCancel,
  onSubmit,
  suggest,
}: {
  action: CfoAction;
  open: boolean;
  canAnswer: boolean;
  prefill: CfoActionRequest | null;
  onOpen: () => void;
  onCancel: () => void;
  onSubmit: Submit;
  suggest: Suggest;
}) {
  const context = [action.founder, action.item].filter(Boolean).join(" · ");
  return (
    <li className="rounded-lg border border-amber-200/80 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{action.title}</p>
          {context && <p className="mt-0.5 text-xs text-slate-600">{context}</p>}
          {action.detail && (
            <p className="mt-0.5 text-xs text-slate-500">{action.detail}</p>
          )}
          {action.impact && (
            <p className="mt-1 text-xs text-slate-500">{action.impact}</p>
          )}
        </div>
        {canAnswer && !open && (
          <Button variant="outline" size="sm" onClick={onOpen}>
            {action.kind === "FOUNDER_PLACE" ? "Document" : "Answer"}
          </Button>
        )}
      </div>
      {open &&
        canAnswer &&
        (action.kind === "FOUNDER_PLACE" ? (
          <FounderPlaceForm
            action={action}
            prefill={prefill}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        ) : (
          <AnswerForm
            action={action}
            onCancel={onCancel}
            onSubmit={onSubmit}
            suggest={suggest}
          />
        ))}
    </li>
  );
}

function AnswerForm({
  action,
  onCancel,
  onSubmit,
  suggest,
}: {
  action: CfoAction;
  onCancel: () => void;
  onSubmit: Submit;
  suggest: Suggest;
}) {
  const [value, setValue] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<CfoPlaceSuggestion | "loading" | null>(null);
  // A place's or a school's country: a country picker, a suggestion, an optional reason.
  const asksCountry = action.kind === "PLACE_COUNTRY" || action.kind === "SCHOOL_COUNTRY";
  const locked = useRef(false);
  const choices = action.choices ?? [];

  // A place's country: a confident suggestion pre-selects the answer while the
  // reviewer has chosen nothing; a less confident one is only offered (a guess
  // from the name alone, e.g. "San Carlos"). Without one, the question stays.
  useEffect(() => {
    if (!asksCountry) return;
    let live = true;
    setSuggestion("loading");
    suggest(action.id)
      .then((found) => {
        if (!live) return;
        setSuggestion(found);
        const pick = found.value;
        // A school is never pre-set as "not a place of study": the model reads short
        // school names as places ("Torrey Pines"); only a country is pre-selected.
        const preset =
          found.status === "SUGGESTED" ||
          (found.status === "NOT_A_PLACE" && action.kind !== "SCHOOL_COUNTRY");
        if (pick && found.confidence === "high" && preset) {
          setValue((current) => current ?? pick);
        }
      })
      .catch(() => {
        if (live) setSuggestion(null);
      });
    return () => {
      live = false;
    };
  }, [action.id, action.kind, asksCountry, suggest]);
  // A place's country may go without justification; a scoring input needs one.
  const optional = asksCountry;
  const typed = reason.trim().length;
  const short = optional ? typed > 0 && typed < MIN_REASON : typed < MIN_REASON;
  const reasonId = `cfo-action-reason-${action.id}`;

  async function save() {
    if (value === null || short || locked.current) return;
    locked.current = true;
    setBusy(true);
    try {
      await onSubmit(value, reason);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  return (
    <form
      className="mt-4 space-y-3 border-t border-slate-100 pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="space-y-1.5">
        <Label>
          {action.kind === "SCHOOL_COUNTRY"
            ? "Country of this school"
            : asksCountry
              ? "Country of this place"
              : "Value"}
        </Label>
        {asksCountry ? (
          <div className="max-w-sm">
            <CfoCountryPicker
              choices={choices}
              value={value}
              onChange={setValue}
              disabled={busy}
            />
            <SuggestionHint
              suggestion={suggestion}
              noun={action.kind === "SCHOOL_COUNTRY" ? "school" : "place"}
              choices={choices}
              value={value}
              disabled={busy}
              onPick={setValue}
            />
          </div>
        ) : (
          <div role="radiogroup" aria-label="Value" className="flex flex-wrap gap-2">
            {choices.map((choice) => (
              <Button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={value === choice.value}
                variant={value === choice.value ? "primary" : "outline"}
                size="sm"
                disabled={busy}
                onClick={() => setValue(choice.value)}
              >
                {choice.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={reasonId}>
          Justification
          {optional && <span className="ml-1 font-normal text-slate-400">(optional)</span>}
        </Label>
        <Textarea
          id={reasonId}
          rows={2}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder={
            action.kind === "SCHOOL_COUNTRY"
              ? "Where you checked it, e.g. “School website: Westview High School, San Diego”"
              : "Where you checked it, e.g. “LinkedIn profile: Irvine, California”"
          }
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-xs text-slate-500">
          Recorded with your name as a manual source
          {short ? ` · at least ${MIN_REASON} characters` : ""}.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={value === null || short || busy}
        >
          {busy && <Loader2 className="animate-spin" />}
          Save answer
        </Button>
      </div>
    </form>
  );
}

const CONFIDENCE: Record<string, string> = {
  high: "high confidence",
  medium: "medium confidence",
  low: "low confidence",
};

/** What the model suggested for a place's country, and that it is only a suggestion. */
function SuggestionHint({
  suggestion,
  noun,
  choices,
  value,
  disabled,
  onPick,
}: {
  suggestion: CfoPlaceSuggestion | "loading" | null;
  /** What the model was given: a place's label or a school's name. */
  noun: "place" | "school";
  choices: NonNullable<CfoAction["choices"]>;
  value: string | null;
  disabled: boolean;
  onPick: (value: string) => void;
}) {
  if (suggestion === null || (suggestion !== "loading" && suggestion.status === "UNAVAILABLE")) {
    return null;
  }
  if (suggestion === "loading") {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Looking for a suggestion…
      </p>
    );
  }
  const name = (code: string) => choices.find((c) => c.value === code)?.label ?? code;
  const picks = [suggestion.value, ...(suggestion.candidates ?? [])].filter(
    (code, i, all): code is string =>
      !!code && all.indexOf(code) === i && choices.some((c) => c.value === code),
  );
  const confident =
    suggestion.confidence === "high" &&
    (suggestion.status === "SUGGESTED" || (suggestion.status === "NOT_A_PLACE" && noun === "place"));
  const headline =
    suggestion.status === "UNKNOWN" || !suggestion.value
      ? `No suggestion for this ${noun}`
      : confident
        ? `Suggested from the ${noun} name alone: ${name(suggestion.value)}`
        : `Possible from the ${noun} name alone, ${
            CONFIDENCE[suggestion.confidence ?? "low"]
          }: ${name(suggestion.value)}`;
  return (
    <div className="mt-1.5 space-y-1 text-xs text-slate-600">
      <p className="flex items-start gap-1.5">
        <Sparkles className="mt-px h-3.5 w-3.5 shrink-0 text-violet-500" />
        <span>
          {headline}
          {confident && suggestion.confidence ? ` · ${CONFIDENCE[suggestion.confidence]}` : ""}.
          {suggestion.reason && <span className="text-slate-500"> {suggestion.reason}</span>}
        </span>
      </p>
      {picks.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-5">
          {picks.map((code) => (
            <Button
              key={code}
              type="button"
              size="sm"
              variant={value === code ? "primary" : "outline"}
              className="h-6 px-2 text-xs"
              disabled={disabled}
              onClick={() => onPick(code)}
            >
              {name(code)}
            </Button>
          ))}
        </div>
      )}
      <p className="pl-5 text-slate-500">
        {confident ? "Pre-selected. " : ""}Check it on the founder's profile before saving:
        your answer is the proof.
      </p>
    </div>
  );
}

const WEB_ADDRESS = /^https?:\/\/[^\s/]+\.[^\s]+$/i;

/** A founder's place nobody documented: whose, what documents it, where it is,
 *  its country and where it was read. Only confirmed founders, accepted kinds
 *  of item and accepted countries are offered: nothing else can count. */
function FounderPlaceForm({
  action,
  prefill,
  onCancel,
  onSubmit,
}: {
  action: CfoAction;
  prefill: CfoActionRequest | null;
  onCancel: () => void;
  onSubmit: Submit;
}) {
  const founders = action.founders ?? [];
  const kinds = action.proof_kinds ?? [];
  const [founder, setFounder] = useState<string | null>(
    prefill?.founderId ?? (founders.length === 1 ? founders[0].value : null),
  );
  const [kind, setKind] = useState<CfoProofKind | null>(null);
  const [place, setPlace] = useState("");
  const [item, setItem] = useState("");
  const [country, setCountry] = useState<string | null>(null);
  const [source, setSource] = useState(prefill?.sourceUrl ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const id = `cfo-founder-place-${action.id}`;
  // The cited source stands for the justification, which stays possible.
  const typed = reason.trim().length;
  const short = typed > 0 && typed < MIN_REASON;
  const sourceValid = WEB_ADDRESS.test(source.trim());
  const ready =
    founder !== null &&
    kind !== null &&
    place.trim().length >= 2 &&
    country !== null &&
    sourceValid &&
    !short;

  async function save() {
    if (!ready || locked.current || !founder || !kind || !country) return;
    locked.current = true;
    setBusy(true);
    try {
      await onSubmit(country, reason, {
        founder_id: founder,
        proof_kind: kind,
        place: place.trim(),
        item: item.trim() || null,
        source_url: source.trim(),
      });
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  return (
    <form
      aria-label="Document a founder's place"
      className="mt-4 space-y-4 border-t border-slate-100 pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium text-slate-900">Founder</legend>
        <div role="radiogroup" aria-label="Founder" className="flex flex-wrap gap-2">
          {founders.map((choice) => (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={founder === choice.value}
              disabled={busy}
              onClick={() => setFounder(choice.value)}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-left text-sm transition-colors",
                founder === choice.value
                  ? "border-indigo-500 bg-indigo-50 text-indigo-900"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              )}
            >
              <span className="font-medium">{choice.label}</span>
              {(choice.aliases ?? []).length > 0 && (
                <span className="block text-xs text-slate-500">
                  {(choice.aliases ?? []).join(" · ")}
                </span>
              )}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium text-slate-900">What places them there</legend>
        <div role="radiogroup" aria-label="What places them there" className="flex flex-wrap gap-2">
          {kinds.map((choice) => (
            <Button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={kind === choice.value}
              variant={kind === choice.value ? "primary" : "outline"}
              size="sm"
              disabled={busy}
              onClick={() => setKind(choice.value as CfoProofKind)}
            >
              {choice.label}
            </Button>
          ))}
        </div>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-place`}>Place</Label>
          <Input
            id={`${id}-place`}
            value={place}
            maxLength={200}
            disabled={busy}
            placeholder="e.g. “Paris” or “HEC Paris”"
            onChange={(e) => setPlace(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Country</Label>
          <CfoCountryPicker
            choices={action.choices ?? []}
            value={country}
            onChange={setCountry}
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-item`}>
            Item <span className="font-normal text-slate-400">(optional)</span>
          </Label>
          <Input
            id={`${id}-item`}
            value={item}
            maxLength={200}
            disabled={busy}
            placeholder="e.g. “MBA” or “Engineer at Criteo”"
            onChange={(e) => setItem(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-source`}>Where you read it</Label>
          <Input
            id={`${id}-source`}
            type="url"
            value={source}
            maxLength={500}
            disabled={busy}
            placeholder="https://www.linkedin.com/in/…"
            aria-invalid={source.trim() !== "" && !sourceValid}
            onChange={(e) => setSource(e.target.value)}
          />
          {source.trim() !== "" && !sourceValid && (
            <p className="text-xs text-rose-600">A web address, starting with https://</p>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-reason`}>
          Justification <span className="font-normal text-slate-400">(optional)</span>
        </Label>
        <Textarea
          id={`${id}-reason`}
          rows={2}
          maxLength={500}
          value={reason}
          disabled={busy}
          placeholder="What you read, e.g. “LinkedIn education: MBA, HEC Paris, 2012–2014”"
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-xs text-slate-500">
          Recorded with your name as a manual source
          {short ? ` · at least ${MIN_REASON} characters` : ""}.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={!ready || busy}>
          {busy && <Loader2 className="animate-spin" />}
          Save place
        </Button>
      </div>
    </form>
  );
}
